import { fetchPantoneResource } from './pantoneAuth';

export interface PantoneLabValues {
  l: number;
  a: number;
  b: number;
}

export interface PantoneCmykValues {
  c: number;
  m: number;
  y: number;
  k: number;
}

export interface PantoneSwatch {
  id: string;
  name: string;
  displayName: string;
  collection?: string;
  finish?: string;
  hex: string;
  lab?: PantoneLabValues;
  cmyk?: PantoneCmykValues;
  metadata?: Record<string, unknown>;
}

export interface PantoneServiceResult {
  matches: PantoneSwatch[];
  licensingNotice?: string;
}

export type PantoneServiceEndpoint = 'search' | 'nearest';

export class PantoneServiceError extends Error {
  readonly status?: number;
  readonly code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'PantoneServiceError';
    this.status = status;
    this.code = code;
  }
}

const DEFAULT_NOTICE =
  'Pantone data is licensed content. Access requires an active Pantone Connect subscription and may be revoked at any time.';

function resolvePantoneApiBase(): string {
  const base = import.meta.env.VITE_PANTONE_API_BASE as string | undefined;
  if (!base) {
    throw new PantoneServiceError(
      'Pantone API base URL is not configured. Define VITE_PANTONE_API_BASE in your environment.',
      undefined,
      'config-missing',
    );
  }
  return base.replace(/\/?$/, '');
}

function normaliseHex(value: unknown): string {
  if (typeof value !== 'string') return '#000000';
  const trimmed = value.trim();
  if (trimmed.startsWith('#')) return trimmed.toUpperCase();
  return `#${trimmed}`.toUpperCase();
}

function normaliseSwatch(entry: unknown, fallbackIndex: number): PantoneSwatch {
  const candidate = (entry && typeof entry === 'object') ? (entry as Record<string, unknown>) : {};

  const idSource = candidate.id ?? candidate.code ?? candidate.name ?? `unknown-${fallbackIndex}`;
  const nameSource = candidate.name ?? candidate.displayName ?? String(idSource ?? `Pantone ${fallbackIndex}`);
  const displaySource = candidate.displayName ?? candidate.name ?? String(idSource ?? `Pantone ${fallbackIndex}`);

  const labCandidate = candidate.lab;
  const lab: PantoneLabValues | undefined =
    labCandidate && typeof labCandidate === 'object'
      ? {
          l: Number((labCandidate as Record<string, unknown>).l ?? 0),
          a: Number((labCandidate as Record<string, unknown>).a ?? 0),
          b: Number((labCandidate as Record<string, unknown>).b ?? 0),
        }
      : undefined;

  const cmykCandidate = candidate.cmyk;
  const cmyk: PantoneCmykValues | undefined =
    cmykCandidate && typeof cmykCandidate === 'object'
      ? {
          c: Number((cmykCandidate as Record<string, unknown>).c ?? 0),
          m: Number((cmykCandidate as Record<string, unknown>).m ?? 0),
          y: Number((cmykCandidate as Record<string, unknown>).y ?? 0),
          k: Number((cmykCandidate as Record<string, unknown>).k ?? 0),
        }
      : undefined;

  return {
    id: String(idSource),
    name: String(nameSource),
    displayName: String(displaySource),
    collection: typeof candidate.collection === 'string' ? candidate.collection : undefined,
    finish: typeof candidate.finish === 'string' ? candidate.finish : undefined,
    hex: normaliseHex(candidate.hex),
    lab,
    cmyk,
    metadata: candidate.metadata && typeof candidate.metadata === 'object' ? (candidate.metadata as Record<string, unknown>) : undefined,
  } satisfies PantoneSwatch;
}

async function handlePantoneResponse(response: Response): Promise<PantoneServiceResult> {
  let payload: Record<string, unknown> = {};
  try {
    const text = await response.text();
    payload = text ? JSON.parse(text) : {};
  } catch {
    if (response.ok) {
      throw new PantoneServiceError('Pantone service returned an unreadable payload.', response.status, 'invalid-json');
    }
  }

  if (!response.ok) {
    const message =
      (payload && typeof payload === 'object' && 'message' in payload && typeof payload.message === 'string')
        ? payload.message
        : response.status === 401
        ? 'Pantone access token is invalid or expired. Please sign in again.'
        : response.status === 403
        ? 'Your Pantone Connect account does not grant access to this resource. Confirm your subscription status.'
        : response.status === 404
        ? 'Pantone swatch not found. Verify the identifier and try again.'
        : `Pantone service returned ${response.status}.`;
    throw new PantoneServiceError(message, response.status);
  }

  const rawMatches: unknown[] = Array.isArray(payload?.matches)
    ? payload.matches
    : Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.results)
    ? payload.results
    : [];

  const licensingNotice =
    typeof payload?.notice === 'string'
      ? payload.notice
      : typeof payload?.licensingNotice === 'string'
      ? payload.licensingNotice
      : DEFAULT_NOTICE;

  const matches = rawMatches.map((entry, index) => normaliseSwatch(entry, index));

  return { matches, licensingNotice } satisfies PantoneServiceResult;
}

async function pantoneFetch(endpoint: PantoneServiceEndpoint, init: RequestInit & { signal?: AbortSignal }): Promise<PantoneServiceResult> {
  const base = resolvePantoneApiBase();
  const target = `${base}/${endpoint}`;

  try {
    const response = await fetchPantoneResource(target, {
      method: init.method ?? 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...init.headers,
      },
      body: init.body,
      signal: init.signal,
    });
    return await handlePantoneResponse(response);
  } catch (error) {
    if (error instanceof PantoneServiceError) {
      throw error;
    }
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new PantoneServiceError('Unable to reach Pantone services. Check your network connection.', undefined, 'network');
    }
    throw new PantoneServiceError(error instanceof Error ? error.message : String(error));
  }
}

export async function searchPantoneSwatches(query: string, signal?: AbortSignal): Promise<PantoneServiceResult> {
  if (!query.trim()) {
    return { matches: [], licensingNotice: DEFAULT_NOTICE } satisfies PantoneServiceResult;
  }

  return pantoneFetch('search', {
    method: 'POST',
    body: JSON.stringify({ query }),
    signal,
  });
}

export async function findNearestPantone(hex: string, options?: { limit?: number; signal?: AbortSignal }): Promise<PantoneServiceResult> {
  const normalised = normaliseHex(hex);
  return pantoneFetch('nearest', {
    method: 'POST',
    body: JSON.stringify({ hex: normalised, limit: options?.limit ?? 6 }),
    signal: options?.signal,
  });
}
